import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { ref, push, onValue, off, set, remove, get } from 'firebase/database';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { realtimeDb, db } from '../config/firebase';
import { useAuth } from './AuthContext';

interface SupportMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: 'user' | 'admin';
  timestamp: number;
}

interface SupportTicket {
  id: string;
  odaId: string;
  odaAdi: string;
  userId: string;
  username: string;
  subject: string;
  initialMessage: string;
  status: 'waiting' | 'active' | 'closed';
  createdAt: number;
  assignedAdmin?: string;
  assignedAdminName?: string;
}

interface LiveSupportContextType {
  activeTicket: SupportTicket | null;
  tickets: SupportTicket[];
  messages: SupportMessage[];
  isConnected: boolean;
  isLoadingTicket: boolean;
  createTicket: (subject: string, initialMessage: string) => Promise<string>;
  sendSupportMessage: (text: string) => Promise<void>;
  closeTicket: (saveToHistory?: boolean) => Promise<void>;
  joinTicket: (ticketId: string) => Promise<void>;
  leaveTicket: () => void;
  adminCloseTicket: (ticketId: string) => Promise<void>;
}

const LiveSupportContext = createContext<LiveSupportContextType>({} as LiveSupportContextType);

export const useLiveSupport = () => {
  return useContext(LiveSupportContext);
};

interface LiveSupportProviderProps {
  children: ReactNode;
}

export const LiveSupportProvider: React.FC<LiveSupportProviderProps> = ({ children }) => {
  const { currentUser, userData, isAdmin } = useAuth();
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingTicket, setIsLoadingTicket] = useState(true);

  // Bağlantı durumunu kontrol et
  useEffect(() => {
    const connectedRef = ref(realtimeDb, '.info/connected');
    onValue(connectedRef, (snapshot) => {
      setIsConnected(snapshot.val() === true);
    });

    return () => off(connectedRef);
  }, []);

  // Admin için tüm açık ticketları dinle
  useEffect(() => {
    if (!currentUser || !isAdmin) return;

    const ticketsRef = ref(realtimeDb, 'supportTickets');
    onValue(ticketsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const ticketList: SupportTicket[] = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).filter(t => t.status !== 'closed');

        // En yeniler üstte
        ticketList.sort((a, b) => b.createdAt - a.createdAt);
        setTickets(ticketList);
      } else {
        setTickets([]);
      }
    });

    return () => off(ticketsRef);
  }, [currentUser, isAdmin]);

  // Kullanıcı için aktif ticket ID'sini takip et
  const [userTicketId, setUserTicketId] = useState<string | null>(null);

  // Admin için aktif ticket ID'sini takip et
  const [adminTicketId, setAdminTicketId] = useState<string | null>(null);

  // Kullanıcının aktif ticket ID'sini dinle
  useEffect(() => {
    if (!currentUser || isAdmin) {
      setIsLoadingTicket(false);
      return;
    }

    setIsLoadingTicket(true);
    const userTicketRef = ref(realtimeDb, `userActiveTicket/${currentUser.uid}`);

    onValue(userTicketRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTicketId(snapshot.val());
      } else {
        setUserTicketId(null);
        setActiveTicket(null);
        setIsLoadingTicket(false);
      }
    });

    return () => off(userTicketRef);
  }, [currentUser, isAdmin]);

  // Ticket detaylarını gerçek zamanlı dinle (kullanıcı için)
  useEffect(() => {
    if (!currentUser || isAdmin || !userTicketId) return;

    const ticketRef = ref(realtimeDb, `supportTickets/${userTicketId}`);

    onValue(ticketRef, (snapshot) => {
      if (snapshot.exists()) {
        const ticketData = snapshot.val();
        setActiveTicket({ id: userTicketId, ...ticketData });
      } else {
        setActiveTicket(null);
        setUserTicketId(null);
      }
      setIsLoadingTicket(false);
    });

    return () => off(ticketRef);
  }, [currentUser, isAdmin, userTicketId]);

  // Admin için ticket detaylarını gerçek zamanlı dinle
  useEffect(() => {
    if (!currentUser || !isAdmin) return;

    if (!adminTicketId) {
      // Admin ticket ID yoksa activeTicket'ı temizle (sadece admin için)
      setActiveTicket(null);
      setMessages([]);
      return;
    }

    const ticketRef = ref(realtimeDb, `supportTickets/${adminTicketId}`);

    onValue(ticketRef, (snapshot) => {
      if (snapshot.exists()) {
        const ticketData = snapshot.val();
        setActiveTicket({ id: adminTicketId, ...ticketData });
      } else {
        // Ticket silindi
        setActiveTicket(null);
        setAdminTicketId(null);
      }
    });

    return () => off(ticketRef);
  }, [currentUser, isAdmin, adminTicketId]);

  // Aktif ticket mesajlarını dinle
  useEffect(() => {
    if (!activeTicket) {
      setMessages([]);
      return;
    }

    const messagesRef = ref(realtimeDb, `supportMessages/${activeTicket.id}`);

    onValue(messagesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const messageList: SupportMessage[] = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        messageList.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(messageList);
      } else {
        setMessages([]);
      }
    });

    return () => {
      off(messagesRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTicket?.id]);

  // Yeni ticket oluştur
  const createTicket = async (subject: string, initialMessage: string): Promise<string> => {
    if (!currentUser || !userData) throw new Error('Kullanıcı girişi gerekli');

    const ticketData: Omit<SupportTicket, 'id'> = {
      odaId: currentUser.uid,
      odaAdi: userData.username || 'Kullanıcı',
      userId: currentUser.uid,
      username: userData.username || 'Kullanıcı',
      subject,
      initialMessage,
      status: 'waiting',
      createdAt: Date.now()
    };

    // Ticket oluştur
    const ticketsRef = ref(realtimeDb, 'supportTickets');
    const newTicketRef = push(ticketsRef);
    await set(newTicketRef, ticketData);

    // Kullanıcının aktif ticketını kaydet
    const userTicketRef = ref(realtimeDb, `userActiveTicket/${currentUser.uid}`);
    await set(userTicketRef, newTicketRef.key);

    // İlk mesajı gönder
    const messagesRef = ref(realtimeDb, `supportMessages/${newTicketRef.key}`);
    await push(messagesRef, {
      text: initialMessage,
      senderId: currentUser.uid,
      senderName: userData.username || 'Kullanıcı',
      senderRole: 'user',
      timestamp: Date.now()
    });

    return newTicketRef.key!;
  };

  // Mesaj gönder
  const sendSupportMessage = async (text: string): Promise<void> => {
    if (!currentUser || !userData || !activeTicket) return;

    const messagesRef = ref(realtimeDb, `supportMessages/${activeTicket.id}`);
    await push(messagesRef, {
      text,
      senderId: currentUser.uid,
      senderName: userData.username || 'Kullanıcı',
      senderRole: isAdmin ? 'admin' : 'user',
      timestamp: Date.now()
    });
  };

  // Ticketı kapat (kullanıcı veya admin)
  const closeTicket = useCallback(async (saveToHistory: boolean = true): Promise<void> => {
    if (!activeTicket) return;

    // Geçmişe kaydet
    if (saveToHistory && messages.length > 0) {
      try {
        await addDoc(collection(db, 'PastSupportChat'), {
          odaId: activeTicket.odaId,
          odaAdi: activeTicket.odaAdi,
          odaKonu: activeTicket.subject,
          odaIlkMesaj: activeTicket.initialMessage,
          odaOlusturmaTarihi: Timestamp.fromMillis(activeTicket.createdAt),
          odaKapatmaTarihi: Timestamp.now(),
          userId: activeTicket.userId,
          username: activeTicket.username,
          assignedAdmin: activeTicket.assignedAdmin || null,
          assignedAdminName: activeTicket.assignedAdminName || null,
          messages: messages.map(m => ({
            text: m.text,
            senderId: m.senderId,
            senderName: m.senderName,
            senderRole: m.senderRole,
            timestamp: Timestamp.fromMillis(m.timestamp)
          }))
        });
      } catch (error) {
        console.error('Geçmiş kaydedilemedi:', error);
      }
    }

    // Realtime DB'den sil
    const ticketRef = ref(realtimeDb, `supportTickets/${activeTicket.id}`);
    const messagesRef = ref(realtimeDb, `supportMessages/${activeTicket.id}`);
    const userTicketRef = ref(realtimeDb, `userActiveTicket/${activeTicket.userId}`);

    await remove(messagesRef);
    await remove(ticketRef);
    await remove(userTicketRef);

    setActiveTicket(null);
    setMessages([]);
  }, [activeTicket, messages]);

  // Admin bir ticketa katıl
  const joinTicket = async (ticketId: string): Promise<void> => {
    if (!currentUser || !userData || !isAdmin) return;

    // Ticket'ı güncelle
    const ticketRef = ref(realtimeDb, `supportTickets/${ticketId}`);
    const ticketSnap = await get(ticketRef);

    if (ticketSnap.exists()) {
      const ticketData = ticketSnap.val();
      await set(ticketRef, {
        ...ticketData,
        status: 'active',
        assignedAdmin: currentUser.uid,
        assignedAdminName: userData.username || 'Admin'
      });

      // Admin ticket ID'sini set et - bu useEffect'i tetikleyecek
      setAdminTicketId(ticketId);
    }
  };

  // Admin ticketı bırak
  const leaveTicket = () => {
    setAdminTicketId(null);
    setActiveTicket(null);
    setMessages([]);
  };

  // Admin ticketı kapat
  const adminCloseTicket = async (ticketId: string): Promise<void> => {
    // Önce ticket bilgilerini al
    const ticketRef = ref(realtimeDb, `supportTickets/${ticketId}`);
    const ticketSnap = await get(ticketRef);

    if (!ticketSnap.exists()) return;

    const ticketData = ticketSnap.val();

    // Mesajları al
    const messagesRef = ref(realtimeDb, `supportMessages/${ticketId}`);
    const messagesSnap = await get(messagesRef);

    const ticketMessages: SupportMessage[] = [];
    if (messagesSnap.exists()) {
      const data = messagesSnap.val();
      Object.keys(data).forEach(key => {
        ticketMessages.push({ id: key, ...data[key] });
      });
    }

    // Geçmişe kaydet
    if (ticketMessages.length > 0) {
      try {
        await addDoc(collection(db, 'PastSupportChat'), {
          odaId: ticketData.odaId,
          odaAdi: ticketData.odaAdi,
          odaKonu: ticketData.subject,
          odaIlkMesaj: ticketData.initialMessage,
          odaOlusturmaTarihi: Timestamp.fromMillis(ticketData.createdAt),
          odaKapatmaTarihi: Timestamp.now(),
          userId: ticketData.userId,
          username: ticketData.username,
          assignedAdmin: ticketData.assignedAdmin || null,
          assignedAdminName: ticketData.assignedAdminName || null,
          messages: ticketMessages.map(m => ({
            text: m.text,
            senderId: m.senderId,
            senderName: m.senderName,
            senderRole: m.senderRole,
            timestamp: Timestamp.fromMillis(m.timestamp)
          }))
        });
      } catch (error) {
        console.error('Geçmiş kaydedilemedi:', error);
      }
    }

    // Realtime DB'den sil
    const userTicketRef = ref(realtimeDb, `userActiveTicket/${ticketData.userId}`);

    await remove(messagesRef);
    await remove(ticketRef);
    await remove(userTicketRef);

    if (activeTicket?.id === ticketId) {
      setAdminTicketId(null);
      setActiveTicket(null);
      setMessages([]);
    }
  };

  const value = {
    activeTicket,
    tickets,
    messages,
    isConnected,
    isLoadingTicket,
    createTicket,
    sendSupportMessage,
    closeTicket,
    joinTicket,
    leaveTicket,
    adminCloseTicket
  };

  return (
    <LiveSupportContext.Provider value={value}>
      {children}
    </LiveSupportContext.Provider>
  );
};
